/**
 * Type guard that checks if a given value is a plain object (but not an array or null).
 * This utility helps differentiate objects from arrays and null values in runtime checks.
 * It returns `true` if the value is of type `'object'`, is not `null`, and is not an array.
 * It is commonly used in recursive data traversal or when validating untyped input structures.
 *
 * @param value The value to test.
 * @returns `true` if the value is a non-null, non-array object; otherwise `false`.
 *
 * @example
 * ```ts
 * if (isObject(data)) {
 *   // safely access object keys
 * }
 * ```
 */
export const isObject = (value: any): value is Record<string, any> => value !== null
    && typeof value === 'object'
    && !Array.isArray(value);

/**
 * Determines whether a value can be merged (i.e., is an object or an array).
 * This utility is useful in deep merge operations where only mergeable types such as plain objects and arrays
 * should be processed recursively. It uses `isObject` to detect non-array objects and explicitly checks for arrays.
 *
 * @param value The value to test for mergeability.
 * @returns `true` if the value is either a plain object or an array; otherwise `false`.
 *
 * @example
 * ```ts
 * if (isMergeable(input)) {
 *   deepMerge(target, input);
 * }
 * ```
 */
export const isMergeable = (value: any): boolean => isObject(value) || Array.isArray(value);